import { type FC, type KeyboardEvent, useState } from 'react';
import Input from '../atoms/Input';
import Icon from '../atoms/Icon';

export interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  id?: string;
}

export const TagInput: FC<TagInputProps> = ({
  tags,
  onTagsChange,
  placeholder = "Type a tag and press Enter to add",
  maxTags,
  disabled = false,
  className = '',
  error = false,
  id
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag if input is empty and backspace is pressed
      removeTag(tags[tags.length - 1]);
    }
  };

  const addTag = (tag: string) => {
    const cleanTag = tag.replace(',', '').toLowerCase().trim();
    
    if (cleanTag.length === 0) {
      return;
    }
    if (tags.includes(cleanTag)) {
      return;
    }
    if (maxTags && tags.length >= maxTags) {
      return;
    }
    
    onTagsChange([...tags, cleanTag]);
    setInputValue('');
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const isMaxReached = maxTags && tags.length >= maxTags;

  return (
    <div className={className}>
      {/* Tags Display */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 text-xs bg-action-key-default text-white rounded-md group"
            >
              <span>{tag}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-1 p-0.5 hover:bg-action-key-darker rounded-sm transition-colors focus:outline-none focus:ring-1 focus:ring-white"
                  aria-label={`Remove ${tag} tag`}
                >
                  <Icon name="close" size="xs" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input Field */}
      <Input
        id={id}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={isMaxReached ? `Maximum ${maxTags} tags reached` : placeholder}
        disabled={disabled || !!isMaxReached}
        error={error}
        className="w-full"
      />

      {/* Helper Text */}
      <div className="flex justify-between items-center mt-1">
        <p className="text-xs text-text-muted">
          Press Enter or comma to add tags
        </p>
        {maxTags && (
          <p className="text-xs text-text-muted">
            {tags.length}/{maxTags}
          </p>
        )}
      </div>
    </div>
  );
};

TagInput.displayName = 'TagInput';

export default TagInput;